<script lang="ts">
  import { api, ApiError, type Backup } from "@/api";
  import { token } from "@/stores/session";
  import { confirmModal } from "@/stores/modal";
  import { balance, feedback } from "@/stores/dashboard";
  import { BACKUP_SLOT_COST, transitionalStatuses } from "@/config";
  import { formatBytes, formatDateTime } from "@/utils/format";

  const {
    serverId,
    serverStatus,
    enabled,
    slots = 0,
    onUpdated,
  }: {
    serverId: string;
    serverStatus: string;
    enabled: boolean;
    slots?: number;
    onUpdated?: () => void;
  } = $props();

  let backups = $state<Backup[]>([]);
  let busy = $state<string | null>(null);
  let loading = $state(true);
  let loaded = $state(false);
  let loadError = $state("");
  let actionError = $state("");
  let notice = $state("");
  let currentSlots = $derived(slots > 0 ? slots : enabled ? 1 : 0);
  let downloadLink = $state("");
  let refreshAgain = false;
  let requestSequence = 0;
  let pendingRequest: { id: string; sequence: number } | null = null;

  const backupStatusLabels: Record<string, string> = {
    queued: "Ожидает создания",
    running: "Создаётся",
    restoring: "Восстанавливается",
    ready: "Готова",
    failed: "Не удалось создать копию",
  };
  const activeBackupsCount = $derived(
    backups.filter((backup) => backup.status !== "failed").length,
  );
  const slotsFull = $derived(currentSlots > 0 && activeBackupsCount >= currentSlots);
  const canRestore = $derived(serverStatus === "stopped");
  const hasPending = $derived(
    backups.some((backup) => ["queued", "running", "restoring"].includes(backup.status)),
  );
  const creatingDisabled = $derived.by(() => {
    const backupsUnavailable = busy !== null || loading || !loaded || Boolean(loadError);
    return backupsUnavailable || hasPending || slotsFull || transitionalStatuses.has(serverStatus);
  });

  function message(cause: unknown, fallback: string) {
    return cause instanceof ApiError ? cause.message : fallback;
  }

  async function refresh(signal?: AbortSignal) {
    if (!$token) {
      return;
    }
    const requestedId = serverId;
    if (pendingRequest?.id === requestedId) {
      refreshAgain = true;
      return;
    }
    const sequence = ++requestSequence;
    pendingRequest = { id: requestedId, sequence };
    loading = true;
    try {
      const result = await api.backups($token, requestedId, signal);
      if (signal?.aborted || requestedId !== serverId || sequence !== requestSequence) {
        return;
      }
      backups = result;
      loaded = true;
      loadError = "";
    } catch (cause) {
      if (signal?.aborted || requestedId !== serverId || sequence !== requestSequence) {
        return;
      }
      loadError = message(cause, "Не удалось загрузить резервные копии");
    } finally {
      if (pendingRequest?.sequence === sequence) {
        pendingRequest = null;
      }
      if (sequence === requestSequence) {
        loading = false;
        if (refreshAgain) {
          refreshAgain = false;
          void refresh();
        }
      }
    }
  }

  async function create() {
    if (!$token || creatingDisabled) {
      return;
    }
    busy = "create";
    actionError = "";
    notice = "";
    try {
      await api.createBackup($token, serverId);
      notice = "Копия поставлена в очередь. Её состояние обновится автоматически.";
      await refresh();
    } catch (cause) {
      actionError = message(cause, "Не удалось поставить копию в очередь");
    } finally {
      busy = null;
    }
  }

  async function purchase() {
    if (!$token || busy) {
      return;
    }
    if ($balance < BACKUP_SLOT_COST) {
      actionError = `Недостаточно кредитов: нужно ${BACKUP_SLOT_COST}, баланс ${$balance}`;
      feedback.set({ kind: "error", text: actionError });
      return;
    }
    const title = currentSlots === 0 ? "Купить слот для копий?" : "Купить дополнительный слот?";
    if (
      !(await confirmModal(
        title,
        `Всего слотов после покупки: ${currentSlots + 1}. Каждый слот хранит одну копию. Дополнительный слот стоит ${BACKUP_SLOT_COST} кредитов.`,
        `Купить за ${BACKUP_SLOT_COST} кр.`,
      ))
    ) {
      return;
    }
    if (!$token || busy) {
      return;
    }
    busy = "purchase";
    actionError = "";
    notice = "";
    try {
      const result = await api.purchaseBackupSlot($token, serverId);
      balance.set(result.balance);
      currentSlots = result.backupSlots;
      notice = "Слот куплен. Теперь можно создать резервную копию.";
      feedback.set({
        kind: "success",
        text: `Слот резервных копий куплен. Всего слотов: ${result.backupSlots}.`,
      });
      onUpdated?.();
    } catch (cause) {
      actionError = message(cause, "Не удалось купить слот резервных копий");
    } finally {
      busy = null;
    }
  }

  async function restore(backup: Backup) {
    const actionUnavailable = !$token || busy;
    if (actionUnavailable || hasPending || !canRestore) {
      return;
    }
    if (
      !(await confirmModal(
        "Восстановить из копии?",
        `Файлы сервера будут заменены копией от ${formatDateTime(backup.createdAt)}. Текущие изменения будут потеряны.`,
        "Восстановить",
      ))
    ) {
      return;
    }
    if (!$token || busy || !canRestore) {
      return;
    }
    busy = `restore:${backup.id}`;
    actionError = "";
    notice = "";
    try {
      await api.restoreBackup($token, serverId, backup.id);
      notice =
        "Восстановление поставлено в очередь. Дождитесь его завершения перед запуском сервера.";
      await refresh();
      onUpdated?.();
    } catch (cause) {
      actionError = message(cause, "Не удалось запустить восстановление");
    } finally {
      busy = null;
    }
  }

  async function remove(backup: Backup) {
    if (!$token || busy || backup.status === "restoring") {
      return;
    }
    const pending = backup.status === "queued" || backup.status === "running";
    if (
      !(await confirmModal(
        pending ? "Отменить создание копии?" : "Удалить резервную копию?",
        pending
          ? "Операция создания резервной копии будет отменена."
          : `Копия от ${formatDateTime(backup.createdAt)} будет удалена без возможности восстановления.`,
        pending ? "Отменить создание" : "Удалить",
      ))
    ) {
      return;
    }
    if (!$token || busy) {
      return;
    }
    busy = `remove:${backup.id}`;
    actionError = "";
    notice = "";
    try {
      await api.deleteBackup($token, serverId, backup.id);
      notice = pending ? "Создание копии отменено." : "Резервная копия удалена.";
      await refresh();
    } catch (cause) {
      actionError = message(
        cause,
        pending ? "Не удалось отменить создание копии" : "Не удалось удалить резервную копию",
      );
    } finally {
      busy = null;
    }
  }

  async function download(backup: Backup) {
    if (!$token || busy) {
      return;
    }
    // Open during the click so browsers do not block the window after the API request.
    const target = window.open("", "_blank");
    if (target) {
      target.opener = null;
    }
    busy = `download:${backup.id}`;
    actionError = "";
    downloadLink = "";
    try {
      const result = await api.downloadBackup($token, serverId, backup.id);
      const url = new URL(result.url);
      if (!["http:", "https:"].includes(url.protocol)) {
        throw new Error("invalid download URL");
      }
      if (target) {
        target.location.href = url.href;
      } else {
        downloadLink = url.href;
      }
    } catch (cause) {
      target?.close();
      actionError = message(cause, "Не удалось получить ссылку на скачивание");
    } finally {
      busy = null;
    }
  }

  $effect(() => {
    void serverId;
    backups = [];
    loaded = false;
    loadError = "";
    actionError = "";
    notice = "";
    downloadLink = "";
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => {
      controller.abort();
      requestSequence++;
      pendingRequest = null;
      refreshAgain = false;
    };
  });
  $effect(() => {
    if (!$token || !hasPending) {
      return;
    }
    const controller = new AbortController();
    // A hidden tab does not poll; it catches up once when it becomes visible again.
    const interval = window.setInterval(() => {
      if (!document.hidden) {
        void refresh(controller.signal);
      }
    }, 4_000);
    const visibility = () => {
      if (!document.hidden) {
        void refresh(controller.signal);
      }
    };
    document.addEventListener("visibilitychange", visibility);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", visibility);
    };
  });
</script>

<section class="panel-card backup-panel" aria-labelledby="backups-title" aria-busy={loading}>
  <header class="panel-heading">
    <div>
      <p class="eyebrow">Защита мира и конфигурации</p>
      <h2 id="backups-title">Резервные копии</h2>
      <p class="backup-summary">
        {currentSlots === 0
          ? "Купите слот, чтобы хранить копии сервера."
          : `Занято слотов: ${activeBackupsCount} из ${currentSlots}`}
      </p>
    </div>
    <div class="backup-controls">
      <button
        class="ghost-button"
        type="button"
        onclick={() => void refresh()}
        disabled={loading || busy !== null}>{loading ? "Обновляем…" : "Обновить"}</button
      >
      <button
        class={currentSlots === 0 ? "button" : "ghost-button"}
        type="button"
        onclick={purchase}
        disabled={busy !== null}
      >
        {busy === "purchase" ? "Покупаем…" : `Купить слот · ${BACKUP_SLOT_COST} кр.`}
      </button>
      {#if currentSlots > 0}
        <button class="button" type="button" onclick={create} disabled={creatingDisabled}
          >{busy === "create" ? "Создаём…" : "Создать копию"}</button
        >
      {/if}
    </div>
  </header>

  {#if loadError}
    <div class="backup-message error" role="alert">
      <p>{loadError}</p>
      <button class="ghost-button" type="button" onclick={() => void refresh()} disabled={loading}
        >{loading ? "Обновляем…" : "Повторить загрузку"}</button
      >
      {#if loaded}<small>Ниже показаны ранее загруженные данные.</small>{/if}
    </div>
  {/if}
  {#if actionError}<p class="backup-message error" role="alert">{actionError}</p>{/if}
  {#if notice}<p class="backup-message success" role="status">{notice}</p>{/if}
  {#if downloadLink}
    <p class="backup-message" role="status">
      Браузер не открыл окно скачивания. <a
        href={downloadLink}
        target="_blank"
        rel="noopener noreferrer">Скачать архив</a
      >
    </p>
  {/if}

  {#if loading && !loaded}
    <div class="backup-empty" role="status">
      <span class="loading-orbit" aria-hidden="true"></span>
      <p>Загружаем резервные копии…</p>
    </div>
  {:else if loaded}
    {#if slotsFull}<p class="backup-message">
        Все слоты заняты. Удалите ненужную копию или купите дополнительный слот.
      </p>{/if}
    {#if backups.length === 0}
      <div class="backup-empty">
        <h3>Копий пока нет</h3>
        <p>
          {currentSlots > 0
            ? "Создайте первую копию перед изменением сборки или важных файлов."
            : `Один слот хранит одну копию и стоит ${BACKUP_SLOT_COST} кредитов. Покупка слота не создаёт копию автоматически.`}
        </p>
      </div>
    {:else}
      <div class="backup-list">
        {#each backups as backup (backup.id)}
          <article class="backup-row">
            <div>
              <strong>{formatDateTime(backup.createdAt)}</strong>
              <small
                >{backupStatusLabels[backup.status] ?? "Состояние обновляется"}{backup.sizeBytes
                  ? ` · ${formatBytes(backup.sizeBytes)}`
                  : ""}</small
              >
              {#if backup.error}<small class="error">{backup.error}</small>{/if}
            </div>
            <div class="backup-actions">
              {#if backup.status === "ready"}
                <button type="button" onclick={() => void download(backup)} disabled={busy !== null}
                  >{busy === `download:${backup.id}` ? "Готовим ссылку…" : "Скачать"}</button
                >
                <button
                  type="button"
                  onclick={() => void restore(backup)}
                  disabled={busy !== null || hasPending || !canRestore}
                  >{busy === `restore:${backup.id}` ? "Восстанавливаем…" : "Восстановить"}</button
                >
              {/if}
              <button
                type="button"
                onclick={() => void remove(backup)}
                disabled={busy !== null || backup.status === "restoring"}
              >
                {busy === `remove:${backup.id}`
                  ? "Отправляем…"
                  : backup.status === "queued" || backup.status === "running"
                    ? "Отменить создание"
                    : "Удалить"}
              </button>
            </div>
          </article>
        {/each}
      </div>
    {/if}
  {/if}
  {#if hasPending}<p class="backup-message" role="status">
      Операция выполняется. Список обновляется автоматически.
    </p>{/if}
  {#if currentSlots > 0 && !canRestore}<p class="backup-message">
      Чтобы восстановить копию, сначала остановите сервер.
    </p>{/if}
</section>

<style>
  .backup-summary {
    margin: 0.65rem 0 0;
    color: var(--muted);
    font-size: 0.85rem;
    line-height: 1.5;
  }
  .backup-controls,
  .backup-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .backup-controls {
    justify-content: flex-end;
  }
  .backup-message {
    margin: 0;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--line);
    color: var(--muted);
    font-size: 0.9rem;
    line-height: 1.5;
  }
  .backup-message p {
    margin: 0 0 0.5rem;
  }
  .backup-message small {
    display: block;
    margin-top: 0.5rem;
  }
  .error {
    color: var(--mars-light);
  }
  .success {
    color: var(--green);
  }
  .backup-empty {
    display: grid;
    justify-items: center;
    gap: 0.75rem;
    padding: 2rem 1.5rem;
    text-align: center;
    color: var(--muted);
  }
  .backup-empty h3,
  .backup-empty p {
    margin: 0;
  }
  .backup-empty p {
    max-width: 48ch;
    line-height: 1.6;
  }
  .backup-actions button {
    min-height: 44px;
    padding: 0.6rem 0.75rem;
    font-size: 0.85rem;
  }
  @media (max-width: 640px) {
    .panel-heading {
      align-items: flex-start;
      flex-direction: column;
    }
    .backup-controls {
      justify-content: flex-start;
    }
  }
</style>
